<template>
  <div class="trialFeatureInfo">
    <div class="featureSection">
      <div class="featurePicContainer">
        <img
          :alt="`${FeatureTextConsts.featureTitle} icon`"
          class="featurePic"
          :data-test-id="$testId('featureImage')"
          :src="featureImageSource"
        >
        <div
          v-if="showPricing && price"
          class="pricing"
        >
          <span>{{ period }} Pricing</span>
          <span
            :data-test-id="$testId('pricing')"
          >${{ price }}/{{ priceBasis }}/{{ billingPeriod }}
          </span>
        </div>
      </div>
    </div>
    <div class="gridSection">
      <div class="planFeatures">
        {{ FeatureTextConsts.featureTitle }} include:
      </div>
      <ul
        class="featureGrid"
        :data-test-id="$testId('featureGrid')"
        icon="checkmark"
      >
        <li
          v-for="featureName in FeatureTextConsts.includedFeatures"
          :key="featureName"
          class="featureName"
        >
          <Icon
            class="icon"
            icon="checkmark"
          />
          <span>{{ featureName }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { Icon } from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';

import { FeatureIllustrationUrl, FeatureTrialTextConsts, trialPropsData } from 'client/util/Constants/FeatureTrialConstants';

export default {
  name: 'TrialFeatureInfo',

  components: {
    Icon,
  },

  props: {
    featureCode: {
      type: String,
      required: true,
    },
    showPricing: {
      type: Boolean,
    },
  },

  computed: {
    ...mapGetters('AddOnCollection', {
      getAddOn: 'getItem',
    }),

    ...mapGetters('SubscriptionCollection', {
      getSubscription: 'getItem',
    }),

    ...mapGetters('SettingsModule', [
      'hasBilling',
    ]),

    ...mapState('PlanModule', [
      'plan',
    ]),

    billingPeriod() {
      return this.isAnnualPricing ? 'year' : 'month';
    },

    featureImageSource() {
      return `${FeatureIllustrationUrl}/${this.featureCode}.svg`;
    },

    isAnnualPricing() {
      return this.plan.contractType === 'Annual';
    },

    period() {
      return this.isAnnualPricing ? 'Annual' : 'Monthly';
    },

    price() {
      const planData = trialPropsData[this.featureCode].isAddOn
        ? this.getAddOn(this.productCode)
        : this.getSubscription(this.productCode);
      const price = this.isAnnualPricing ? get(planData, 'annualPrice', null) : get(planData, 'listPrice', null);

      return price;
    },

    priceBasis() {
      return FeatureTrialTextConsts[this.featureCode].priceBasis || 'user';
    },
  },

  created() {
    this.FeatureTextConsts = FeatureTrialTextConsts[this.featureCode];
    this.productCode = trialPropsData[this.featureCode].productCode;

    if (this.hasBilling) {
      this.fetchPlan();
    }

    this.fetchAddOns();
    this.fetchSubscriptions();
  },

  methods: {
    ...mapActions('AddOnCollection', {
      fetchAddOns: 'fetchCollection',
    }),

    ...mapActions('SubscriptionCollection', {
      fetchSubscriptions: 'fetchCollection',
    }),

    ...mapActions('PlanModule', ['fetchPlan']),
  },
};
</script>

<style scoped>
.featureGrid {
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  grid-gap: 10px;
  grid-template-rows: repeat(3, minmax(0, 1fr));
  margin-top: var(--jc-spacer);
  padding-left: 0;
}

.featurePic {
  max-width: 300px;
  width: 100%;
}

.featurePicContainer {
  display: inline-block;
  padding: 0 var(--jc-spacer-small);
  text-align: center;
}

.featureSection {
  display: flex;
  justify-content: center;
  width: 100%;
}

.gridSection {
  display: inline-block;
  padding: var(--jc-spacer-small);
}

.planFeatures {
  font-size: var(--jcBody);
  margin-top: var(--jc-spacer-medium);
}

.pricing {
  display: flex;
  font-size: var(--jcBody);
  justify-content: space-between;
  padding-top: var(--jc-spacer-small);
}

.featureName {
  display: flex;
  list-style-type: none;
  padding-left: 0;
}

.featureName span {
  font-size: var(--jcBodySmall);
}

.featureName .icon {
  margin-right: var(--jc-spacer-small);
}

.featureGrid .featureName /deep/ svg {
  height: 8px;
  width: 8px;
}
</style>
